import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiEye, FiEyeOff } from "react-icons/fi";

/**
 * Allowed Domains Manager
 * Lets merchants add/remove domains that are allowed to make browser-based API calls.
 * Used in both merchant dashboard (self-service) and admin panel (admin override).
 */
const AllowedDomainsManager = ({ merchantId = null, isAdmin = false }) => {
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    fetchDomains();
  }, [merchantId]);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      let response;
      if (isAdmin && merchantId) {
        // Admin viewing a specific merchant's domains
        const res = await axios.get(`/api/merchant/config/${merchantId}/info`);
        const domain = res.data?.data?.domain || '';
        setDomains(domain ? domain.split(',').map(d => d.trim()).filter(Boolean) : []);
      } else {
        // Merchant viewing their own domains
        response = await axios.get('/api/merchant/config/my-domains');
        setDomains(response.data?.data || []);
      }
    } catch (error) {
      showMessage('Failed to load domains', 'error');
    } finally {
      setLoading(false);
    }
  };

  const refreshCorsCache = async () => {
    try { await axios.post('/api/admin/cors/refresh'); } catch (_) { /* non-critical */ }
  };

  const handleAdd = async () => {
    const domain = newDomain.trim().toLowerCase()
      .replace(/^https?:\/\//, '') // strip protocol
      .replace(/\/.*$/, '');       // strip path

    if (!domain) {
      showMessage('Please enter a domain', 'error');
      return;
    }

    if (domains.includes(domain)) {
      showMessage('Domain already added', 'error');
      return;
    }

    setSaving(true);
    try {
      if (isAdmin && merchantId) {
        const updated = [...domains, domain];
        await axios.put(`/api/merchant/config/admin/${merchantId}/domains`, { domains: updated });
        setDomains(updated);
      } else {
        const response = await axios.post('/api/merchant/config/my-domains', { domain });
        setDomains(response.data?.data || [...domains, domain]);
      }
      setNewDomain('');
      await refreshCorsCache();
      showMessage('Domain added — CORS updated immediately', 'success');
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to add domain', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (domain) => {
    if (!window.confirm(`Remove domain "${domain}"?`)) return;

    setSaving(true);
    try {
      if (isAdmin && merchantId) {
        const updated = domains.filter(d => d !== domain);
        await axios.put(`/api/merchant/config/admin/${merchantId}/domains`, { domains: updated });
        setDomains(updated);
      } else {
        const response = await axios.delete(`/api/merchant/config/my-domains?domain=${encodeURIComponent(domain)}`);
        setDomains(response.data?.data || domains.filter(d => d !== domain));
      }
      await refreshCorsCache();
      showMessage('Domain removed', 'success');
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to remove domain', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  if (loading) return <div style={{ padding: '10px', color: '#666' }} role='status' aria-live='polite'>Loading domains...</div>;

  return (
    <div style={{ padding: '16px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff' }}>
      <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>🌐 Allowed Domains (CORS)</h3>
      <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#666' }}>
        Domains allowed to make browser-based API calls. Your server-side integrations are not affected.
      </p>

      {message.text && (
        <div style={{
          padding: '8px 12px', marginBottom: '12px', borderRadius: '4px', fontSize: '13px',
          background: message.type === 'error' ? '#fdecea' : '#e8f5e9',
          color: message.type === 'error' ? '#c62828' : '#2e7d32'
        }}>
          {message.text}
        </div>
      )}

      {/* Add domain */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <label htmlFor="new-domain-input" className="sr-only">Add allowed domain</label>
        <input
          id="new-domain-input"
          type="text"
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          placeholder="e.g. shop.yoursite.com"
          aria-label="Add allowed domain"
          style={{ flex: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' }}
        />
        <button
          onClick={handleAdd}
          disabled={saving || !newDomain.trim()}
          style={{
            padding: '8px 16px', background: '#007a75', color: '#fff', border: 'none',
            borderRadius: '4px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px'
          }}
        >
          {saving ? '...' : 'Add'}
        </button>
      </div>

      {/* Domain list */}
      {domains.length === 0 ? (
        <p style={{ color: '#767676', fontSize: '13px', margin: 0 }}>No domains added yet.</p>
      ) : (
        <div>
          {domains.map(domain => (
            <div key={domain} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 12px', marginBottom: '6px', background: '#f5f5f5',
              borderRadius: '4px', fontSize: '14px'
            }}>
              <span style={{ fontFamily: 'monospace' }}>{domain}</span>
              <button
                onClick={() => handleDelete(domain)}
                disabled={saving}
                aria-label={`Remove domain ${domain}`}
                style={{
                  padding: '4px 10px', background: '#d32f2f', color: '#fff', border: 'none',
                  borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AllowedDomainsManager;
